import React, { useEffect, useState } from "react";
import { SpecialZoomLevel, Worker, Viewer } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "../../components/vfs_fonts";
import * as services from "../../services/saleng";
import { useParams } from "react-router-dom";
import { Button } from "@mui/material";

pdfMake.vfs = pdfFonts.pdfMake.vfs;
pdfMake.fonts = {
  THSarabun: {
    normal: "THSarabunNew.ttf",
    bold: "THSarabunNew_Bold.ttf",
    italics: "THSarabunNew_Italic.ttf",
    bolditalics: "THSarabunNew_Bold.ttf",
  },
};

const formatNumberWithCommas = (num) =>
  num?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

const getBase64FromUrl = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Unable to load logo (${response.status})`);
  }
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const ReceiptPDF = () => {
  const { transactionId } = useParams();
  const [pdfUrl, setPdfUrl] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchReceipt = async () => {
      if (!transactionId) return;

      try {
        setError("");
        const response = await services.getTransaction(transactionId);
        const receiptData = response.data.data;
        if (!receiptData) {
          throw new Error("ไม่พบข้อมูลใบเสร็จ");
        }

        let base64Logo = null;
        const logoUrl = receiptData.branch?.image || receiptData.shop?.logo;
        if (logoUrl) {
          try {
            base64Logo = await getBase64FromUrl(logoUrl);
          } catch (logoError) {
            console.warn("Unable to load receipt logo", logoError);
          }
        }

        const items = Array.isArray(receiptData.items) ? receiptData.items : [];
        const branchName = receiptData.branch?.name?.trim() || "-";
        const branchAddress = receiptData.branch?.address?.trim() || "-";

        const docDefinition = {
          pageSize: "A5",
          pageOrientation: "portrait",
          pageMargins: [15, 15, 15, 15],
          defaultStyle: {
            font: "THSarabun",
            fontSize: 13,
          },
          content: [
            ...(base64Logo
              ? [
                  {
                    image: base64Logo,
                    width: 50,
                    alignment: "center",
                    margin: [0, 0, 0, 4],
                  },
                ]
              : []),
            {
              columns: [
                {
                  width: "*",
                  stack: [
                    {
                      text: branchName,
                      fontSize: 17,
                      bold: true,
                    },
                    {
                      text: branchAddress,
                      fontSize: 12,
                      margin: [0, 1, 0, 0],
                    },
                  ],
                },
                {
                  width: "auto",
                  text: "ใบเสร็จรับเงิน",
                  alignment: "right",
                  fontSize: 16,
                  bold: true,
                },
              ],
              margin: [0, 0, 0, 4],
            },
            {
              canvas: [
                {
                  type: "line",
                  x1: 0,
                  y1: 0,
                  x2: 390,
                  y2: 0,
                  lineWidth: 0.7,
                  lineColor: "#333333",
                },
              ],
              margin: [0, 0, 0, 6],
            },
            {
              text: [
                { text: "เลขที่ใบเสร็จ: ", bold: true },
                receiptData.billNumber || "-",
                "\n",
                { text: "วันที่: ", bold: true },
                new Date(receiptData.createdAt).toLocaleDateString("th-TH", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                }),
                "\n",
                { text: "ลูกค้า: ", bold: true },
                receiptData.customerName || "-",
              ],
              margin: [0, 0, 0, 6],
            },
            {
              table: {
                widths: ["*", "auto", "auto", "auto"],
                body: [
                  [
                    { text: "รายการสินค้า", bold: true },
                    { text: "น้ำหนัก (กก.)", alignment: "right", bold: true },
                    {
                      text: "ราคา (บาท/กก.)",
                      alignment: "right",
                      bold: true,
                    },
                    {
                      text: "ราคารวม (บาท)",
                      alignment: "right",
                      bold: true,
                    },
                  ],
                  ...items.map((item) => [
                    { text: item.productName },
                    {
                      text: formatNumberWithCommas(Number(item.weight) || 0),
                      alignment: "right",
                    },
                    {
                      text: formatNumberWithCommas(item.pricePerUnit),
                      alignment: "right",
                    },
                    {
                      text: formatNumberWithCommas(item.total),
                      alignment: "right",
                    },
                  ]),
                ],
              },
              layout: {
                fillColor: (rowIndex) => (rowIndex === 0 ? "#eeeeee" : null),
                hLineWidth: () => 0.5,
                vLineWidth: () => 0.5,
              },
              margin: [0, 0, 0, 12],
            },
            {
              table: {
                widths: ["*", "auto"],
                body: [
                  [
                    "",
                    {
                      text: `ยอดรวมทั้งหมด: ${formatNumberWithCommas(
                        receiptData.totalAmount || 0
                      )} บาท`,
                      alignment: "right",
                      bold: true,
                      margin: [0, 2, 6, 2],
                    },
                  ],
                ],
              },
              layout: "noBorders",
            },
            {
              columns: [
                { width: "*", text: "" },
                {
                  width: "auto",
                  stack: [
                    {
                      text: "ลงชื่อผู้รับเงิน ................................................",
                      margin: [0, 20, 0, 0],
                    },
                  ],
                  alignment: "right",
                },
              ],
              margin: [0, 6, 0, 0],
            },
          ],
        };

        pdfMake.createPdf(docDefinition).getDataUrl((dataUrl) => {
          setPdfUrl(dataUrl);
        });
      } catch (error) {
        console.error("Error generating receipt:", error);
        setError("ไม่สามารถเปิดใบเสร็จได้ กรุณาลองใหม่อีกครั้ง");
      }
    };

    fetchReceipt();
  }, [transactionId]);

  const downloadPdf = () => {
    if (!pdfUrl) return;

    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = `receipt_${transactionId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div
      style={{
        height: "100vh",
        width: "100vw",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ flex: 1, overflow: "auto", paddingBottom: "60px" }}>
        {error ? (
          <p>{error}</p>
        ) : pdfUrl ? (
          <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
            <Viewer fileUrl={pdfUrl} defaultScale={SpecialZoomLevel.PageFit} />
          </Worker>
        ) : (
          <p>กำลังโหลดใบเสร็จ...</p>
        )}
      </div>

      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          width: "100%",
          padding: "10px",
          backgroundColor: "#fff",
          borderTop: "1px solid #ccc",
          textAlign: "center",
          zIndex: 1000,
        }}
      >
        <Button
          onClick={downloadPdf}
          style={{
            padding: "10px 20px",
            fontSize: "16px",
            cursor: "pointer",
          }}
        >
          ดาวน์โหลด PDF
        </Button>
      </div>
    </div>
  );
};

export default ReceiptPDF;
